import { isInvoiceOverdue } from "../utils/invoiceDueDate";
import { outstandingInvoices } from "../utils/invoiceOutstanding";
import { invoiceMonthlyTotals } from "../utils/invoiceMonthlyTotals";
import DashboardCommercialSummary from "../components/DashboardCommercialSummary";
import PageHeading from "../components/PageHeading";
import React from "react";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../axios/AxiosInstance";
import { apiErrorMessage } from "../utils/apiError";
import { TrendingUp } from "lucide-react";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { loadAtradiusReviews } from "../services/atradiusReview";

const revenueAmount = (value) =>
  value !== null &&
  value !== undefined &&
  value !== "" &&
  Number.isFinite(Number(value))
    ? new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value))
    : "—";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [invoiceError, setInvoiceError] = useState("");
  const [revenueLoading, setRevenueLoading] = useState(true);
  useEffect(() => {
    let active = true;
    AxiosInstance.get("facture/freelance")
      .then(({ data }) => {
        if (!active) return;
        if (!Array.isArray(data)) { setInvoiceError(apiErrorMessage(new Error("La réponse des factures n’est pas une liste."), "Impossible de vérifier les factures.")); return; }
        setInvoices(data);
      })
      .catch((error) => { if (active) setInvoiceError(apiErrorMessage(error, "Impossible de charger les factures.")); })
      .finally(() => { if (active) setRevenueLoading(false); });
    return () => {
      active = false;
    };
  }, []);
  const nombreFacturesImpayees = outstandingInvoices(invoices.filter(invoice => isInvoiceOverdue(invoice))).count;
  const monthParts = new Intl.DateTimeFormat("en", {
    year: "numeric",
    month: "2-digit",
    timeZone: "Europe/Paris",
  }).formatToParts(new Date());
  const defaultMonth = `${monthParts.find((p) => p.type === "year").value}-${monthParts.find((p) => p.type === "month").value}`;
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);
  const monthRow = invoiceMonthlyTotals(invoices).find((row) => row.month === selectedMonth);
  const monthTotalHT = monthRow ? monthRow.amountHT : 0;
  const [atradius, setAtradius] = useState({
    loading: true,
    reviews: [],
    failures: 0,
  });
  useEffect(() => {
    let active = true;
    loadAtradiusReviews()
      .then((result) => {
        if (active) setAtradius({ ...result, loading: false });
      })
      .catch(() => {
        if (active) setAtradius({ loading: false, reviews: [], failures: 1 });
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="w-full">
      <div className="w-full">
        {/* Header Section */}
        <motion.div className="mb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <PageHeading
                icon="dashboard"
                title={<> Bonjour {localStorage.getItem("firstname")} 👋 </>}
                description={
                  <> Voici un aperçu de votre activité aujourd'hui </>
                }
              />
            </div>
          </div>
        </motion.div>
        {invoiceError && <p role="status" className="mb-4 text-sm text-amber-700">{invoiceError}</p>}
        {Number(nombreFacturesImpayees) > 0 && (
          <div
            role="status"
            className="mb-6 rounded-xl border border-rose-200 bg-rose-50 p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h2 className="font-semibold text-rose-900">
                  {nombreFacturesImpayees} facture
                  {Number(nombreFacturesImpayees) > 1 ? "s" : ""} impayée
                  {Number(nombreFacturesImpayees) > 1 ? "s" : ""}
                </h2>
                <p className="mt-1 text-sm text-rose-800">
                  Des paiements sont en attente. Consultez les factures pour
                  suivre les règlements et préparer les relances.
                </p>
              </div>
              <button
                onClick={() => navigate("/facture?impayees=1")}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Voir les factures
              </button>
            </div>
          </div>
        )}
        {!atradius.loading &&
          (atradius.reviews.length > 0 || atradius.failures > 0) && (
            <div
              role="status"
              className="mb-6 rounded-xl border border-orange-200 bg-orange-50 p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="font-semibold text-orange-900">
                    {atradius.reviews.length
                      ? `Atradius à revoir : ${atradius.reviews.length} client${atradius.reviews.length > 1 ? "s" : ""}`
                      : "Vérification Atradius incomplète"}
                  </h2>
                  {atradius.reviews.length > 0 && (
                    <>
                      <p className="mt-1 text-sm text-orange-800">
                        Le chiffre d’affaires HT du mois dépasse la couverture accordée.
                      </p>
                      <p className="mt-2 text-sm font-medium text-orange-900">
                        {atradius.reviews
                          .slice(0, 3)
                          .map(
                            ({ client }) =>
                              client.companyName || client.denomination,
                          )
                          .join(" · ")}
                        {atradius.reviews.length > 3 ? "…" : ""}
                      </p>
                    </>
                  )}
                  {atradius.failures > 0 && (
                    <p className="mt-2 text-xs text-orange-800">
                      Certaines couvertures n’ont pas pu être vérifiées.
                    </p>
                  )}
                </div>
                <button
                  onClick={() => navigate("/atradius")}
                  className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
                >
                  Voir Atradius
                </button>
              </div>
            </div>
          )}
        <section
          aria-labelledby="monthly-revenue-title"
          className="mb-8 rounded-2xl border border-slate-200 bg-white p-6"
        >
          <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-emerald-50 p-3 text-emerald-600">
                <TrendingUp size={22} />
              </div>
              <div>
                <h2
                  id="monthly-revenue-title"
                  className="text-lg font-semibold text-slate-900"
                >
                  Chiffre d’affaires mensuel HT
                </h2>
                <p className="mt-1 text-sm capitalize text-slate-500">
                  {new Date(`${selectedMonth}-01T12:00:00`).toLocaleDateString(
                    "fr-FR",
                    { month: "long", year: "numeric" },
                  )}
                  {" · "}toutes les factures du mois, hors taxes
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-5">
              <div>
                <label
                  htmlFor="revenue-month"
                  className="mb-1 block text-xs font-medium text-slate-500"
                >
                  Choisir le mois
                </label>
                <input lang="fr-FR"
                  id="revenue-month"
                  type="month"
                  value={selectedMonth}
                  onChange={(event) => {
                    if (/^\d{4}-\d{2}$/.test(event.target.value))
                      setSelectedMonth(event.target.value);
                  }}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Total HT du mois</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">
                  {revenueLoading ? "…" : revenueAmount(monthTotalHT)}
                </p>
              </div>
            </div>
          </div>
          {!revenueLoading && monthRow?.htIncomplete && monthRow.amountHT != null && (
            <p role="status" className="mt-4 text-sm text-amber-700">
              Certaines factures de ce mois n’ont pas de montant HT : le total
              additionne uniquement les montants renseignés.
            </p>
          )}
          {!revenueLoading && monthRow && monthRow.amountHT == null && (
            <p role="status" className="mt-4 text-sm text-amber-700">
              Les factures de ce mois n’ont pas de montant HT.
            </p>
          )}
        </section>
        <DashboardCommercialSummary />
      </div>
    </div>
  );
}
